import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { rowMenuPosition } from "../list/ProductActions";

type Props = {
  open: boolean;
  /** The pencil or button that opened it; the popover hangs off its right edge and flips up near the bottom. */
  anchor: DOMRect | null;
  label: string;
  width?: number;
  onClose: () => void;
  children: (close: () => void) => React.ReactNode;
};

/**
 * Tablet and laptop (Figma VT04–VT06, VL04–VL06): a picker opens as a popover next to the row it edits
 * instead of a bottom sheet. Escape and a click outside close it; focus returns to the opener.
 */
const VpPop = ({ open, anchor, label, width = 440, onClose, children }: Props) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    // The opener (the row's button) has focus now; it gets it back on close. Inside, the first field takes focus.
    const previous = document.activeElement as HTMLElement | null;
    const first = panelRef.current?.querySelector<HTMLElement>("input, textarea, select");
    (first ?? panelRef.current)?.focus();
    // Escape closes from anywhere, like PlSheet, so a Tab out of the panel does not strand the popover.
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      previous?.focus?.();
    };
  }, [open]);
  if (!open) return null;
  const position: React.CSSProperties = anchor
    ? rowMenuPosition(anchor, window.innerWidth, window.innerHeight, 480)
    : { position: "fixed", top: 80, right: 21, maxHeight: Math.max(240, window.innerHeight - 120) };
  return createPortal(
    <div
      className="pl-menu-layer vp-pop-layer"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div ref={panelRef} className="pl-menu vp-pop" role="dialog" aria-label={label} tabIndex={-1} style={{ ...position, width }}>
        {children(onClose)}
      </div>
    </div>,
    document.body
  );
};

export default VpPop;
